

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dbHelpers } from '../../db/database';
import { ordersAPI } from '../../api/orders';
import { customersAPI } from '../../api/customers';
import syncService from '../../services/syncService';
import { useToast } from '../../hooks/useToast';
import { useParams } from 'react-router-dom';
import apiClient from '../../api/client';
import { stocksAPI } from '../../api/stocks';
import './OrderForm.css';

function OrderForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const toast = useToast();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [stocks, setStocks] = useState([]);
  
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  
  const [orderData, setOrderData] = useState({
    warehouse: '',
    status: 'pending',
    notes: ''
  });
  
  const [orderItems, setOrderItems] = useState([{
    product: '',
    quantity: 1,
    price: 0
  }]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (isEditMode && products.length > 0 && customers.length > 0 && warehouses.length > 0) {
      loadOrderForEdit(id);
    }
  }, [id, isEditMode, products.length, customers.length, warehouses.length]);

  // async function loadData() {
  //   setLoading(true);
  //   try {
  //     await syncService.fullSync();
      
  //     const [prods, custs, whse] = await Promise.all([
  //       dbHelpers.getProducts(),
  //       dbHelpers.getCustomers(),
  //       dbHelpers.getWarehouses(),
  //       dbHelpers.getStocks()
  //     ]);
      
  //     setProducts(prods);
  //     setCustomers(custs);
  //     setWarehouses(whse);
  //     setStocks(stks);
  //   } catch (error) {
  //     setError('Failed to load data. Please try again.');
  //   }
  //   setLoading(false);
  // }

  async function loadData() {
  setLoading(true);
  try {
    await syncService.fullSync();
    
    const [prods, custs, whse, stks] = await Promise.all([  // ⭐ FIXED
      dbHelpers.getProducts(),
      dbHelpers.getCustomers(),
      dbHelpers.getWarehouses(),
      dbHelpers.getStocks()
    ]);
    
    setProducts(prods);
    setCustomers(custs);
    setWarehouses(whse);
    setStocks(stks);  // ✅ Works now
  } catch (error) {
    setError('Failed to load data. Please try again.');
  }
  setLoading(false);
}

  async function loadOrderForEdit(orderId) {
    try {
      const response = await ordersAPI.getById(orderId);
      const order = response.data;

      // Check if order can be edited
      if (order.status !== 'pending') {
        toast.error('Only pending orders can be edited');
        navigate('/orders');
        return;
      }

      // Populate order data
      setSelectedCustomer(order.customer || order.customer_id);
      setOrderData({
        warehouse: order.warehouse || order.warehouse_id,
        status: order.status,
        notes: order.notes || ''
      });

      // Populate order items
      if (order.items && order.items.length > 0) {
        setOrderItems(order.items.map(item => ({
          id: item.id, // Keep item ID for updates
          product: item.product || item.product_id,
          quantity: item.quantity,
          price: item.price
        })));
      }

    } catch (error) {
      console.error('Failed to load order:', error);
      toast.error('Failed to load order details');
      navigate('/orders');
    }
  }

  async function handleCreateCustomer() {
    setError('');
    setSuccess('');
    if (!newCustomer.name.trim() || !newCustomer.phone.trim()) {
      toast.info('Please fill in customer name and phone');
      return;
    }

    try {
      const customerData = {
        name: newCustomer.name.trim(),
        email: newCustomer.email.trim(),
        phone: newCustomer.phone.trim(),
        address: newCustomer.address.trim(),
        is_active: true
      };

      const response = await customersAPI.create(customerData);
      const customer = response.data;
      
      setCustomers([...customers, customer]);
      setSelectedCustomer(customer.id);
      setShowNewCustomer(false);
      
      await dbHelpers.saveCustomers([customer]);
      
      setNewCustomer({ name: '', email: '', phone: '', address: '' });
      
      setSuccess('Customer created successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message;
      setError(`Failed to create customer: ${errorMsg}`);
    }
  }

  function handleAddItem() {
    setOrderItems([...orderItems, { product: '', quantity: 1, price: 0 }]);
  }

  function handleRemoveItem(index) {
    if (orderItems.length === 1) {
      toast.info('Order must have at least one item');
      return;
    }
    setOrderItems(orderItems.filter((_, i) => i !== index));
  }
// Get total stock for a product across all warehouses
function getProductStock(productId, selectedWarehouseId) {
  const productStocks = stocks.filter(s => 
    String(s.product) === String(productId) &&
    (!selectedWarehouseId || String(s.warehouse) === String(selectedWarehouseId))
  );
  return productStocks.reduce((sum, s) => sum + (s.quantity || 0), 0);
}

// Get stock for a specific variant
function getVariantStock(variantId, selectedWarehouseId) {
  const variantStocks = stocks.filter(s => 
    String(s.variant) === String(variantId) &&
    (!selectedWarehouseId || String(s.warehouse) === String(selectedWarehouseId))
  );
  return variantStocks.reduce((sum, s) => sum + (s.quantity || 0), 0);
}

// Check if item has sufficient stock
function hasStock(productId, variantId, quantity, selectedWarehouseId) {
  if (variantId) {
    const stock = getVariantStock(variantId, selectedWarehouseId);
    return stock >= quantity;
  } else {
    const stock = getProductStock(productId, selectedWarehouseId);
    return stock >= quantity;
  }
}


  const [productVariants, setProductVariants] = useState({});

// ⭐ REPLACE handleItemChange function (around line 186)
async function handleItemChange(index, field, value) {
  const newItems = [...orderItems];
  newItems[index][field] = value;
  
  if (field === 'product') {
    const product = products.find(p => String(p.id) === String(value));
    
    if (product) {
      // Reset variant when product changes
      newItems[index].variant = null;
      newItems[index].price = product.selling_price || 0;
      
      // ⭐ Load variants if product has them
      if (product.has_variants) {
        try {
          const response = await apiClient.get(`/products/${value}/`);
          const productData = response.data;
          
          if (productData.variants && productData.variants.length > 0) {
            console.log('🔍 Variants loaded:', productData.variants);
            setProductVariants(prev => ({
              ...prev,
              [value]: productData.variants
            }));
          }
        } catch (error) {
          console.error('Failed to load variants:', error);
        }
      } else {
        // Clear variants for non-variant products
        setProductVariants(prev => {
          const updated = { ...prev };
          delete updated[value];
          return updated;
        });
      }
    }
  }
  
  // ⭐ Update price when variant is selected
  if (field === 'variant') {
    const productId = newItems[index].product;
    const variants = productVariants[productId] || [];
    const selectedVariant = variants.find(v => String(v.id) === String(value));
    
    if (selectedVariant) {
      newItems[index].price = selectedVariant.selling_price || 0;
    }
  }
  
  setOrderItems(newItems);
}

// ⭐ UPDATE validateForm function (around line 198)
function validateForm() {
  const newErrors = {};

  if (!selectedCustomer) {
    newErrors.customer = 'Please select or create a customer';
  }

  if (!orderData.warehouse) {
    newErrors.warehouse = 'Please select a warehouse';
  }

  if (orderItems.some(item => !item.product)) {
    newErrors.items = 'Please select products for all items';
  }

  // Validate variants
  const hasInvalidVariants = orderItems.some(item => {
    if (!item.product) return false;
    const product = products.find(p => String(p.id) === String(item.product));
    return product?.has_variants && !item.variant;
  });

  if (hasInvalidVariants) {
    newErrors.items = 'Please select a variant for all variant products';
  }

  if (orderItems.some(item => item.quantity <= 0)) {
    newErrors.items = 'Quantity must be greater than 0';
  }

  // ⭐ NEW: Check stock availability
  const outOfStockItems = orderItems.filter(item => {
    if (!item.product || !item.quantity) return false;
    return !hasStock(item.product, item.variant, item.quantity, orderData.warehouse);
  });

  if (outOfStockItems.length > 0) {
    newErrors.items = 'Some items have insufficient stock in the selected warehouse';
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
}



  function calculateTotal() {
    return orderItems.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity || 0) * parseFloat(item.price || 0));
    }, 0);
  }

  function validateForm() {
    const newErrors = {};

    if (!selectedCustomer) {
      newErrors.customer = 'Please select or create a customer';
    }

    if (!orderData.warehouse) {
      newErrors.warehouse = 'Please select a warehouse';
    }

    if (orderItems.some(item => !item.product)) {
      newErrors.items = 'Please select products for all items';
    }

    if (orderItems.some(item => item.quantity <= 0)) {
      newErrors.items = 'Quantity must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // async function handleSubmit(e) {
  //   e.preventDefault();

  //   if (!validateForm()) {
  //     const firstError = Object.values(errors)[0];
  //     toast.error(`❌ ${firstError}`);
  //     return;
  //   }

  //   setSaving(true);

  //   try {
  //     // Build order payload
  //     const orderPayload = {
  //       customer: selectedCustomer,
  //       warehouse: orderData.warehouse,
  //       status: orderData.status,
  //       notes: orderData.notes || '',
  //       items: orderItems.map(item => ({
  //         ...(item.id && { id: item.id }), // Include ID if editing existing item
  //         product: item.product,
  //         quantity: parseInt(item.quantity),
  //         price: parseFloat(item.price)
  //       }))
  //     };

  //     let response;
  //     if (isEditMode) {
  //       // Update existing order
  //       response = await ordersAPI.update(id, orderPayload);
  //       toast.success('✅ Order updated successfully!');
  //     } else {
  //       // Create new order
  //       response = await ordersAPI.create(orderPayload);
  //       toast.success('✅ Order created successfully!');
  //     }

  //     const savedOrder = response.data;

  //     // Save to IndexedDB
  //     await dbHelpers.saveOrders([savedOrder]);
      
  //     // Save order items to IndexedDB
  //     if (savedOrder.items && savedOrder.items.length > 0) {
  //       const itemsToSave = savedOrder.items.map(item => ({
  //         ...item,
  //         order_id: savedOrder.id
  //       }));
  //       await dbHelpers.saveOrderItems(itemsToSave);
  //     }
      
  //     // Sync to get latest data
  //     await syncService.fullSync();

  //     navigate('/orders');
  //   } catch (error) {
  //     let errorMsg = 'Unknown error';
      
  //     if (error.response?.data) {
  //       const errorData = error.response.data;
        
  //       if (typeof errorData === 'object') {
  //         const errors = [];
  //         for (const [field, messages] of Object.entries(errorData)) {
  //           const msgArray = Array.isArray(messages) ? messages : [messages];
  //           errors.push(`${field}: ${msgArray.join(', ')}`);
  //         }
  //         errorMsg = errors.join('\n');
  //       } else {
  //         errorMsg = JSON.stringify(errorData);
  //       }
  //     } else {
  //       errorMsg = error.message;
  //     }
      
  //     toast.error(`❌ Failed to ${isEditMode ? 'update' : 'create'} order:\n\n${errorMsg}`);
  //   }

  //   setSaving(false);
  // }

  async function handleSubmit(e) {
  e.preventDefault();

  if (!validateForm()) {
    const firstError = Object.values(errors)[0];
    toast.error(`❌ ${firstError}`);
    return;
  }

  setSaving(true);

  try {
    // ⭐ Filter out items without products BEFORE sending
    const validItems = orderItems.filter(item => item.product && item.product !== '');
    
    if (validItems.length === 0) {
      toast.error('❌ Please add at least one product to the order');
      setSaving(false);
      return;
    }

    // Build order payload
    const orderPayload = {
      customer: selectedCustomer,
      warehouse: orderData.warehouse,
      status: orderData.status,
      notes: orderData.notes || '',
      items: validItems.map(item => ({  // ⭐ Use validItems, not orderItems
        ...(item.id && { id: item.id }),
        product: item.product,
        variant: item.variant || null,
        quantity: parseInt(item.quantity),
        price: parseFloat(item.price)
      }))
    };

    // ⭐ DEBUG: Log payload
    console.log('📦 Order Payload:', JSON.stringify(orderPayload, null, 2));
    console.log('📋 Items being sent:', orderPayload.items.length);

    let response;
    if (isEditMode) {
      response = await ordersAPI.update(id, orderPayload);
      toast.success('✅ Order updated successfully!');
    } else {
      response = await ordersAPI.create(orderPayload);
      toast.success('✅ Order created successfully!');
    }

    const savedOrder = response.data;
    console.log('✅ Saved Order:', savedOrder);
    console.log('✅ Items received:', savedOrder.items ? savedOrder.items.length : 0);

    // Save to IndexedDB
    await dbHelpers.saveOrders([savedOrder]);
    
    if (savedOrder.items && savedOrder.items.length > 0) {
      const itemsToSave = savedOrder.items.map(item => ({
        ...item,
        order_id: savedOrder.id
      }));
      await dbHelpers.saveOrderItems(itemsToSave);
    }
    
    // ⭐ REMOVE SLOW SYNC - Just reload stocks from cache
    const freshStocks = await dbHelpers.getStocks();
    setStocks(freshStocks);

    navigate('/orders');
  } catch (error) {
    console.error('❌ Order creation error:', error);
    console.error('❌ Error response:', error.response?.data);
    
    let errorMsg = 'Unknown error';
    
    if (error.response?.data) {
      const errorData = error.response.data;
      
      if (typeof errorData === 'object') {
        const errors = [];
        for (const [field, messages] of Object.entries(errorData)) {
          const msgArray = Array.isArray(messages) ? messages : [messages];
          errors.push(`${field}: ${msgArray.join(', ')}`);
        }
        errorMsg = errors.join('\n');
      } else {
        errorMsg = JSON.stringify(errorData);
      }
    } else {
      errorMsg = error.message;
    }
    
    toast.error(`❌ Failed to ${isEditMode ? 'update' : 'create'} order:\n\n${errorMsg}`);
  }

  setSaving(false);
}

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="order-form-container fade-in">
      <div className="breadcrumb">
        <button onClick={() => navigate('/orders')} className="btn-primary-small">
          ← Back to Orders
        </button>
      </div>

      <div className="form-card">
        <h1>📦 {isEditMode ? 'Edit Order' : 'Create New Order'}</h1>
        <p className="subtitle">{isEditMode ? 'Update order details below' : 'Fill in the order details below'}</p>

        <form onSubmit={handleSubmit}>
          {/* Customer Section */}
          <div className="form-section">
            <h3>👤 Customer Information</h3>
            
            {!showNewCustomer ? (
              <>
                <div className="form-group">
                  <label>Customer *</label>
                  <select
                    value={selectedCustomer}
                    onChange={(e) => setSelectedCustomer(e.target.value)}
                    className={`form-input ${errors.customer ? 'error' : ''}`}
                  >
                    <option value="">Select Customer</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} - {c.phone}
                      </option>
                    ))}
                  </select>
                  {errors.customer && <span className="error-text">{errors.customer}</span>}
                </div>
                {!isEditMode && (
                  <button
                    type="button"
                    onClick={() => setShowNewCustomer(true)}
                    className="btn-link-green"
                  >
                    ➕ Add New Customer
                  </button>
                )}
              </>
            ) : (
              <div className="new-customer-form">
                <h4>New Customer</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                      className="form-input"
                      placeholder="Customer name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                      className="form-input"
                      placeholder="+255..."
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                      className="form-input"
                      placeholder="customer@email.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      value={newCustomer.address}
                      onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                      className="form-input"
                      placeholder="Customer address"
                    />
                  </div>
                </div>
                <div className="button-group-inline">
                  <button
                    type="button"
                    onClick={handleCreateCustomer}
                    className="btn btn-primary-small"
                  >
                    Save Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewCustomer(false)}
                    className="btn btn-secondary-small"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Warehouse & Status */}
          <div className="form-section">
            <h3>🏭 Warehouse & Status</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Select Warehouse *</label>
                <select
                  value={orderData.warehouse}
                  onChange={(e) => setOrderData({...orderData, warehouse: e.target.value})}
                  className={`form-input ${errors.warehouse ? 'error' : ''}`}
                >
                  <option value="">Select Warehouse</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
                {errors.warehouse && <span className="error-text">{errors.warehouse}</span>}
              </div>

              <div className="form-group">
                <label>Order Status</label>
                <select
                  value={orderData.status}
                  onChange={(e) => setOrderData({...orderData, status: e.target.value})}
                  className="form-input"
                  disabled={isEditMode} // Disable status change in edit mode
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                </select>
                <span className="help-text">
                  {isEditMode 
                    ? 'Status cannot be changed while editing. Use order details page to change status.' 
                    : 'Note: Confirmed/Delivered orders cannot be modified later'}
                </span>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="form-section">
            <h3>📋 Order Items</h3>
            
           
            {orderItems.map((item, index) => {
  // ⭐ NEW: Get variant info for this item
  const selectedProduct = products.find(p => String(p.id) === String(item.product));
  const hasVariants = selectedProduct?.has_variants;
  const variants = hasVariants ? (productVariants[item.product] || []) : [];
  
  return (
    <div key={index} className="order-item-row">
      <div className="form-group flex-2">
  <label>Product</label>
  <select
    value={item.product}
    onChange={(e) => handleItemChange(index, 'product', e.target.value)}
    className="form-input"
  >
    <option value="">Select Product</option>
    {products.map(p => {
      const stock = getProductStock(p.id, orderData.warehouse);
      const stockInfo = orderData.warehouse ? ` [Stock: ${stock}]` : '';
      
      return (
        <option key={p.id} value={p.id} disabled={!p.has_variants && stock === 0}>
          {p.name} {p.has_variants ? '(Has Variants)' : ''} - TSh {parseFloat(p.selling_price || 0).toFixed(2)}{stockInfo}
          {!p.has_variants && stock === 0 ? ' - OUT OF STOCK' : ''}
        </option>
      );
    })}
  </select>
</div>
      
{hasVariants && (
  <div className="form-group flex-2">
    <label>Variant *</label>
    <select
      value={item.variant || ''}
      onChange={(e) => handleItemChange(index, 'variant', e.target.value)}
      className="form-input"
      required={hasVariants}
    >
      <option value="">Select Variant</option>
      {variants.map(v => {
        const stock = getVariantStock(v.id, orderData.warehouse);
        const stockInfo = orderData.warehouse ? ` [Stock: ${stock}]` : '';
        
        return (
          <option key={v.id} value={v.id} disabled={stock === 0}>
            {v.color} {v.size} {v.material ? `(${v.material})` : ''} - TSh {parseFloat(v.selling_price || 0).toFixed(2)}{stockInfo}
            {stock === 0 ? ' - OUT OF STOCK' : ''}
          </option>
        );
      })}
    </select>
  </div>
)}
      <div className="form-group flex-1">
        <label>Quantity</label>
        <input
          type="number"
          value={item.quantity}
          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
          className="form-input"
          min="1"
        />
      </div>
      
      <div className="form-group flex-1">
        <label>Price</label>
        <input
          type="number"
          value={item.price}
          onChange={(e) => handleItemChange(index, 'price', e.target.value)}
          className="form-input"
          step="0.01"
        />
      </div>
      
      <div className="form-group flex-1">
        <label>Subtotal</label>
        <input
          type="text"
          value={`TSh ${(item.quantity * item.price).toFixed(2)}`}
          className="form-input"
          disabled
        />
      </div>

      <div className="form-group flex-1">
  <label>Subtotal</label>
  <input
    type="text"
    value={`TSh ${(item.quantity * item.price).toFixed(2)}`}
    className="form-input"
    disabled
  />
</div>

{/* ⭐ NEW: Stock warning */}
{item.product && item.quantity > 0 && orderData.warehouse && (
  <div style={{gridColumn: '1 / -1', marginTop: '-8px'}}>
    {!hasStock(item.product, item.variant, item.quantity, orderData.warehouse) && (
      <small style={{color: '#ef4444', fontSize: '0.85em'}}>
        ⚠️ Insufficient stock! Available: {item.variant ? getVariantStock(item.variant, orderData.warehouse) : getProductStock(item.product, orderData.warehouse)}
      </small>
    )}
  </div>
)}
      
      <div className="form-group remove-btn-container">
        {orderItems.length > 1 && (
          <button
            type="button"
            onClick={() => handleRemoveItem(index)}
            className="btn-remove"
            title="Remove item"
          >
            ✖
          </button>
        )}
      </div>
    </div>
  );
})}

            {errors.items && <span className="error-text">{errors.items}</span>}
            
            <button
              type="button"
              onClick={handleAddItem}
              className="btn-add-item"
            >
              ➕ Add Another Item
            </button>
          </div>

          {/* Notes */}
          <div className="form-section">
            <h3>📝 Additional Notes</h3>
            <textarea
              value={orderData.notes}
              onChange={(e) => setOrderData({...orderData, notes: e.target.value})}
              className="form-input"
              rows="3"
              placeholder="Add any special instructions or notes..."
            />
          </div>

          {/* Total */}
          <div className="order-total">
            <span>Total Amount:</span>
            <span className="total-amount">TSh {calculateTotal().toFixed(2)}</span>
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className="btn btn-secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving 
                ? (isEditMode ? 'Updating Order...' : 'Creating Order...') 
                : (isEditMode ? '✅ Update Order' : '✅ Create Order')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OrderForm;